import {createContext, ReactElement, FC, useState, useEffect, useCallback} from "react";
import {useGetMe} from "@/app2/shared/data";
import {useAccessToken} from "@/app2/shared/hooks/useAccessToken";
import i18n from "@/i18n";

/** Updates allowed to locale state. */
export type LocaleContextUpdate = { locale?: string };

const initialState: [LocaleContextState, (update: LocaleContextUpdate) => void] = [
	{locale: undefined},
	() => {}
];

export const LocaleContext = createContext(initialState);

/** Supported app locales (matches API and i18n bundles). */
export const SUPPORTED_LOCALES = ['en', 'es-419'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const DEFAULT_LOCALE: SupportedLocale = 'en';

/** Returns the locale if it is supported, otherwise default 'en'. No mapping/normalization. */
export function resolveLocale(raw: string | undefined | null): SupportedLocale {
	if (!raw || typeof raw !== 'string') return DEFAULT_LOCALE;
	return SUPPORTED_LOCALES.includes(raw as SupportedLocale) ? (raw as SupportedLocale) : DEFAULT_LOCALE;
}

type LocaleContextState = {
	locale?: SupportedLocale
}

interface IProps {
	children: ReactElement
}

/**
 * Locale Provider for App2
 * 
 * Manages i18n language switching based on user's preferred language from API.
 * Fetches `/me` preferred language only when an OAuth access token exists (public routes use localStorage / default).
 * 
 * Features:
 * - Persists locale to localStorage
 * - Automatically switches i18n language
 * - Integrates with React Query cache
 * - Defaults to English if no preference set
 */
export const LocaleProvider: FC<IProps> = ({children}) => {
	const local = localStorage.getItem("locale");
	const localParsed = local ? JSON.parse(local) : false;
	const accessToken = useAccessToken();
	const {data: userData} = useGetMe({enabled: Boolean(accessToken)});
	const [state, setState] = useState<LocaleContextState>(
		localParsed ? {locale: resolveLocale(localParsed)} : {...initialState[0]}
	);

	// Setter: accept only en | es-419, persist to localStorage as-is
	const setLocale = useCallback((update: LocaleContextUpdate) => {
		setState(prev => {
			const nextLocale = update.locale !== undefined ? resolveLocale(update.locale) : prev.locale;
			if (nextLocale !== undefined) {
				localStorage.setItem("locale", JSON.stringify(nextLocale));
			}
			return update.locale !== undefined ? { ...prev, locale: nextLocale } : { ...prev };
		});
	}, []);

	// When user data loads, sync API preferredLanguage to state and localStorage (en | es-419 only)
	useEffect(() => {
		if (userData?.preferredLanguage) {
			const locale = resolveLocale(userData.preferredLanguage);
			setState(prev => ({...prev, locale}));
			localStorage.setItem("locale", JSON.stringify(locale));
		}
	}, [userData?.preferredLanguage]);

	// Update i18n when locale state changes (i18n bundles are "en" and "es-419")
	useEffect(() => {
		i18n.changeLanguage(state.locale ?? DEFAULT_LOCALE);
	}, [state]);

	return (
		<LocaleContext.Provider value={[state, setLocale]}>
			{children}
		</LocaleContext.Provider>
	);
};

